import {
  Alert,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Select,
  Switch,
  Table,
  Typography,
} from 'antd'
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import React, { useEffect, useState } from 'react'
import { FEATURE_TOGGLE_ERROR_TYPES } from 'domain/FeatureToggle/featureToggleApi'
import MoreInfoModal from 'components/MoreInfoModal'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import './FeatureToggleDrawer.less'

const { Text, Title } = Typography
const { TextArea } = Input

const FeatureToggleDrawer = ({
  initialValues,
  isRequestingRolesList,
  isRequestingUserList,
  mode,
  onClose,
  onSubmit,
  open,
  roleOptions,
  specificUsersOptions,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isEnabled, setIsEnabled] = useState(initialValues?.isEnabled || false)
  const [selectedUsernames, setSelectedUsernames] = useState(
    initialValues?.usernames || []
  )
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false)
  const [
    isAccessRestrictionsModalVisible,
    setIsAccessRestrictionsModalVisible,
  ] = useState(false)

  const openHelpModal = () => setIsHelpModalVisible(true)
  const closeHelpModal = () => setIsHelpModalVisible(false)
  const openAccessRestrictionsModal = () =>
    setIsAccessRestrictionsModalVisible(true)
  const closeAccessRestrictionsModal = () =>
    setIsAccessRestrictionsModalVisible(false)

  const isViewMode = mode === 'view'

  useEffect(() => {
    if (open) {
      form.resetFields()
      form.setFieldsValue({
        description: initialValues?.description || '',
        featureCode: initialValues?.featureCode || '',
        featureType: initialValues?.featureType || '',
        isEnabled: initialValues?.isEnabled || false,
        roles: initialValues?.roles || [],
        usernames: initialValues?.usernames || [],
      })
      setIsEnabled(initialValues?.isEnabled || false)
      setSelectedUsernames(initialValues?.usernames || [])
      setShowValidationErrors(false)
      setSubmitError(null)
    }
  }, [open, initialValues, form])

  const handleSubmit = async values => {
    if (onSubmit) {
      await onSubmit(values)
    } else {
      onClose()
    }
  }

  const handleSave = async () => {
    setShowValidationErrors(true)

    let values
    try {
      values = await form.validateFields()
    } catch {
      // form validation failed — field-level errors are already shown
      return
    }

    setShowValidationErrors(false)

    try {
      await handleSubmit(values)
    } catch (error) {
      if (error?.errorType === FEATURE_TOGGLE_ERROR_TYPES.BAD_REQUEST) {
        setSubmitError(t('featureToggle.notifications.createBadRequest'))
      } else if (error?.errorType === FEATURE_TOGGLE_ERROR_TYPES.NOT_FOUND) {
        setSubmitError(t('featureToggle.notifications.notFound'))
      } else {
        setSubmitError(
          error?.message || t('global.error.postRequestErrorDescription')
        )
      }
    }
  }

  const handleChangeEnabled = checked => {
    setIsEnabled(checked)
    form.setFieldsValue({ isEnabled: checked })
  }

  const handleUsernamesChange = values => {
    setSelectedUsernames(values)
    form.setFieldsValue({ usernames: values })
  }

  const handleRemoveUsername = usernameToRemove => {
    const nextUsernames = selectedUsernames.filter(
      username => username !== usernameToRemove
    )

    handleUsernamesChange(nextUsernames)
  }

  const handleValuesChange = changedValues => {
    if (!showValidationErrors) {
      return
    }

    setShowValidationErrors(false)

    const changedFieldNames = Object.keys(changedValues)
    if (changedFieldNames.length > 0) {
      form.setFields(changedFieldNames.map(name => ({ errors: [], name })))
    }
  }

  const descriptionMaxLength = 100
  const renderErrorMessage = text => (
    <span className="feature-toggle-drawer__error-message">
      <ExclamationCircleOutlined className="feature-toggle-drawer__error-icon" />
      {text}
    </span>
  )

  const selectedUsersTableColumns = [
    {
      dataIndex: 'username',
      key: 'username',
    },
    {
      align: 'right',
      key: 'action',
      render: (_, record) => (
        <Button
          aria-label={`Remove ${record.username}`}
          danger
          disabled={!isEnabled}
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveUsername(record.key)}
          size="small"
          type="text"
        />
      ),
    },
  ]

  const selectedUsersTableData = [...selectedUsernames].reverse().map(id => ({
    key: id,
    username: specificUsersOptions.find(o => o.value === id)?.label || id,
  }))

  if (isViewMode) {
    const featureCode = initialValues?.featureCode || '-'
    const description = initialValues?.description || '-'
    const featureType = initialValues?.featureType || '-'
    const enabled = Boolean(initialValues?.isEnabled)

    const usernames = Array.isArray(initialValues?.usernames)
      ? initialValues.usernames
      : []
    const roles = Array.isArray(initialValues?.roles) ? initialValues.roles : []

    const resolvedUsernames = usernames.map(
      id => specificUsersOptions.find(o => o.value === id)?.label || id
    )
    const resolvedRoles = roles.map(
      id => roleOptions.find(o => o.value === id)?.label || id
    )

    return (
      <Drawer
        destroyOnClose
        footer={null}
        onClose={onClose}
        open={open}
        title={t('featureToggle.drawer.title.view')}
        width={480}
      >
        <div className="feature-toggle-drawer feature-toggle-drawer--view">
          <div className="feature-toggle-drawer__view-row">
            <Text className="feature-toggle-drawer__view-label">
              {t('featureToggle.drawer.fields.featureCode')}:
            </Text>
            <Text className="feature-toggle-drawer__view-value">
              {featureCode}
            </Text>
          </div>

          <div className="feature-toggle-drawer__view-row">
            <Text className="feature-toggle-drawer__view-label">
              {t('featureToggle.drawer.fields.featureType')}:
            </Text>
            <Text className="feature-toggle-drawer__view-value">
              {t(`featureToggle.drawer.fields.${featureType}`)}
            </Text>
          </div>

          <div className="feature-toggle-drawer__view-row">
            <Text className="feature-toggle-drawer__view-label">
              {t('featureToggle.drawer.fields.featureStatus')}:
            </Text>
            <Text className="feature-toggle-drawer__view-value">
              {enabled
                ? t('featureToggle.status.enabled')
                : t('featureToggle.status.disabled')}
            </Text>
          </div>

          <div className="feature-toggle-drawer__view-row">
            <Text className="feature-toggle-drawer__view-label">
              {t('featureToggle.drawer.fields.description')}:
            </Text>
            <Text className="feature-toggle-drawer__view-value">
              {description}
            </Text>
          </div>

          <div className="feature-toggle-drawer__view-row">
            <Text className="feature-toggle-drawer__view-label">
              {t('featureToggle.drawer.fields.userRoles')}:
            </Text>
            <Text className="feature-toggle-drawer__view-value">
              {resolvedRoles.length > 0 ? resolvedRoles.join(', ') : '-'}
            </Text>
          </div>

          <div className="feature-toggle-drawer__view-row">
            <Text className="feature-toggle-drawer__view-label">
              {t('featureToggle.drawer.fields.specificUsers')}:
            </Text>
            <Text className="feature-toggle-drawer__view-value">
              {resolvedUsernames.length > 0
                ? resolvedUsernames.join(', ')
                : '-'}
            </Text>
          </div>
        </div>
      </Drawer>
    )
  }

  return (
    <>
      <Drawer
        destroyOnClose
        footer={null}
        onClose={onClose}
        open={open}
        width={480}
        title={
          <span className="feature-toggle-drawer__title">
            {mode === 'edit'
              ? t('featureToggle.drawer.title.edit')
              : t('featureToggle.drawer.title.create')}
            <QuestionCircleOutlined
              className="feature-toggle-drawer__help-icon"
              onClick={openHelpModal}
            />
          </span>
        }
      >
        <div className="feature-toggle-drawer">
          <Form
            className="feature-toggle-drawer__form"
            form={form}
            layout="vertical"
            onValuesChange={handleValuesChange}
            requiredMark={false}
            validateTrigger={[]}
          >
            <div className="feature-toggle-drawer__body">
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const featureCodeErrors = form.getFieldError('featureCode')
                  const hasFeatureCodeError =
                    showValidationErrors && featureCodeErrors.length > 0
                  const featureCodeHelp = hasFeatureCodeError ? (
                    featureCodeErrors[0]
                  ) : (
                    <Text className="feature-field-note" type="secondary">
                      {t('featureToggle.drawer.notes.featureCode')}
                    </Text>
                  )

                  return (
                    <Form.Item
                      help={featureCodeHelp}
                      name="featureCode"
                      validateStatus={hasFeatureCodeError ? 'error' : undefined}
                      label={
                        <span>
                          {t('featureToggle.drawer.fields.featureCode')}
                          <span className="feature-toggle-drawer__required-asterisk">
                            *
                          </span>
                        </span>
                      }
                      rules={
                        mode === 'create'
                          ? [
                              {
                                message: renderErrorMessage(
                                  t(
                                    'featureToggle.drawer.validation.featureCodeRequired'
                                  )
                                ),
                                required: true,
                              },
                            ]
                          : []
                      }
                    >
                      <Input
                        disabled={mode !== 'create'}
                        placeholder={t(
                          'featureToggle.drawer.placeholders.featureCode'
                        )}
                      />
                    </Form.Item>
                  )
                }}
              </Form.Item>

              <Form.Item
                className="feature-toggle-drawer__FeatureType"
                label={t('featureToggle.drawer.fields.featureType')}
                name="featureType"
              >
                <Select
                  options={[
                    {
                      label: t('featureToggle.drawer.fields.frontend'),
                      value: 'frontend',
                    },
                    {
                      label: t('featureToggle.drawer.fields.backend'),
                      value: 'backend',
                    },
                  ]}
                />
              </Form.Item>

              <Form.Item name="isEnabled" valuePropName="checked">
                <Card className="feature-status-card" size="small">
                  <div className="feature-status-container">
                    <div>
                      <div className="feature-status-title">
                        {t('featureToggle.drawer.fields.featureStatus')}
                      </div>
                      <div className="feature-status-subtitle">
                        {isEnabled
                          ? t('featureToggle.drawer.featureStatus.enabled')
                          : t('featureToggle.drawer.featureStatus.disabled')}
                      </div>
                    </div>

                    <Switch
                      checked={isEnabled}
                      onChange={handleChangeEnabled}
                    />
                  </div>
                </Card>
              </Form.Item>

              <Form.Item noStyle shouldUpdate>
                {() => {
                  const descriptionErrors = form.getFieldError('description')
                  const hasDescriptionError =
                    showValidationErrors && descriptionErrors.length > 0
                  const descriptionHelp = hasDescriptionError ? (
                    descriptionErrors[0]
                  ) : (
                    <Text className="feature-field-note" type="secondary">
                      {t('featureToggle.drawer.notes.description')}
                    </Text>
                  )

                  return (
                    <Form.Item
                      help={descriptionHelp}
                      name="description"
                      validateStatus={hasDescriptionError ? 'error' : undefined}
                      label={
                        <span>
                          {t('featureToggle.drawer.fields.description')}
                          <span className="feature-toggle-drawer__required-asterisk">
                            *
                          </span>
                        </span>
                      }
                      rules={[
                        {
                          message: renderErrorMessage(
                            t(
                              'featureToggle.drawer.validation.descriptionRequired'
                            )
                          ),
                          required: true,
                        },
                        {
                          message: renderErrorMessage(
                            t(
                              'featureToggle.drawer.validation.descriptionMinChars'
                            )
                          ),
                          min: 10,
                        },
                      ]}
                    >
                      <TextArea
                        maxLength={descriptionMaxLength}
                        rows={3}
                        placeholder={t(
                          'featureToggle.drawer.placeholders.description'
                        )}
                        showCount={{
                          formatter: ({ count }) =>
                            `${count}/${descriptionMaxLength}`,
                        }}
                      />
                    </Form.Item>
                  )
                }}
              </Form.Item>

              <div
                className={`feature-access-restrictions-section${
                  !isEnabled
                    ? ' feature-access-restrictions-section--disabled'
                    : ''
                }`}
              >
                <div className="feature-access-restrictions-title">
                  <Title level={5}>
                    {t('featureToggle.drawer.fields.accessRestrictions')}
                    <span className="feature-access-restrictions-title__optional">
                      {` (${t('featureToggle.drawer.fields.optional')})`}
                    </span>
                    <QuestionCircleOutlined
                      className="feature-toggle-drawer__help-icon access-restrictions-help-icon"
                      onClick={openAccessRestrictionsModal}
                    />
                  </Title>
                </div>

                <Form.Item
                  label={t('featureToggle.drawer.fields.userRoles')}
                  name="roles"
                >
                  <Select
                    disabled={!isEnabled}
                    loading={isRequestingRolesList}
                    mode="multiple"
                    options={roleOptions}
                    placeholder={t('featureToggle.drawer.placeholders.roles')}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                {/* Hidden field keeps the form store in sync for validateFields() */}
                <Form.Item hidden name="usernames">
                  <Select mode="multiple" />
                </Form.Item>

                <Form.Item
                  label={t('featureToggle.drawer.fields.specificUsers')}
                >
                  <Select
                    className="feature-toggle-drawer__users-select"
                    disabled={!isEnabled}
                    loading={isRequestingUserList}
                    maxTagCount={0}
                    maxTagPlaceholder={null}
                    mode="multiple"
                    onChange={handleUsernamesChange}
                    options={specificUsersOptions}
                    placeholder={t('featureToggle.drawer.placeholders.users')}
                    showSearch
                    value={selectedUsernames}
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                  <Text className="feature-field-note" type="secondary">
                    {t('featureToggle.drawer.notes.onlyRegisteredUsers')}
                  </Text>

                  {selectedUsernames.length > 0 && (
                    <div className="feature-toggle-drawer__selected-users-table">
                      <Table
                        columns={selectedUsersTableColumns}
                        dataSource={selectedUsersTableData}
                        pagination={false}
                        showHeader={false}
                        size="small"
                      />
                    </div>
                  )}
                </Form.Item>
              </div>
            </div>

            <div className="feature-toggle-drawer__actions">
              <Button
                className="feature-toggle-drawer__cancel"
                onClick={onClose}
              >
                {t('featureToggle.drawer.buttons.cancel')}
              </Button>
              <Button onClick={handleSave} type="primary">
                {t('featureToggle.drawer.buttons.saveFeatureToggle')}
              </Button>
            </div>

            {submitError && (
              <Alert
                className="feature-toggle-drawer__submit-error"
                message={submitError}
                showIcon
                type="error"
              />
            )}
          </Form>
        </div>
      </Drawer>

      <MoreInfoModal
        isVisible={isHelpModalVisible}
        onClose={closeHelpModal}
        title={t('featureToggle.drawer.title.info')}
      >
        <div className="feature-toggle-drawer__help-modal">
          {t('featureToggle.drawer.infoTooltip.general')
            .split('\n\n')
            .map((line, i) => (
              <Text
                className="feature-toggle-drawer__info-popover-line"
                key={i}
              >
                {line}
              </Text>
            ))}
        </div>
      </MoreInfoModal>

      <MoreInfoModal
        isVisible={isAccessRestrictionsModalVisible}
        onClose={closeAccessRestrictionsModal}
        title={t('featureToggle.drawer.fields.accessRestrictions')}
      >
        <div className="feature-toggle-drawer__help-modal">
          {t('featureToggle.drawer.infoTooltip.accessRestrictions')
            .split('\n\n')
            .map((line, i) => (
              <Text
                className="feature-toggle-drawer__info-popover-line"
                key={i}
              >
                {line}
              </Text>
            ))}
        </div>
      </MoreInfoModal>
    </>
  )
}

FeatureToggleDrawer.defaultProps = {
  initialValues: {},
  isRequestingRolesList: false,
  isRequestingUserList: false,
  onSubmit: undefined,
  roleOptions: [],
  specificUsersOptions: [],
}

FeatureToggleDrawer.propTypes = {
  initialValues: PropTypes.shape({
    description: PropTypes.string,
    featureCode: PropTypes.string,
    featureType: PropTypes.oneOf(['frontend', 'backend']),
    isEnabled: PropTypes.bool,
    roles: PropTypes.arrayOf(PropTypes.string),
    usernames: PropTypes.arrayOf(PropTypes.string),
  }),
  isRequestingRolesList: PropTypes.bool,
  isRequestingUserList: PropTypes.bool,
  mode: PropTypes.oneOf(['create', 'edit', 'view']).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  open: PropTypes.bool.isRequired,
  roleOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  specificUsersOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
}

export default FeatureToggleDrawer
